import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Building2, Users, Gift, TrendingUp, LogOut, CreditCard, FileText, User, Mail, BarChart3, Crown, PlugZap, Brain } from 'lucide-react';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.header`
  background: white;
  padding: 16px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 12px 0;
    margin-bottom: 20px;
  }
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-start;
    padding: 0 16px;
  }
`;

const Logo = styled.h1`
  color: #00d4aa;
  font-size: 24px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
  }
`;

const UserName = styled.span`
  font-weight: 500;
  color: #333;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #00d4aa;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #00b894;
  }

  @media (max-width: 768px) {
    flex: 1 1 140px;
    justify-content: center;
  }
`;

const TourTriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #ffffff;
  color: #00b894;
  border: 1px solid #00d4aa;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #ecfdf5;
  }

  @media (max-width: 768px) {
    flex: 1 1 140px;
    justify-content: center;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #c82333;
  }

  @media (max-width: 768px) {
    flex: 1 1 140px;
    justify-content: center;
  }
`;

const Main = styled.main`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const WelcomeSection = styled.section`
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 24px;
  }
`;

const WelcomeTitle = styled.h2`
  font-size: 32px;
  color: #333;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const WelcomeSubtitle = styled.p`
  color: #666;
  font-size: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 16px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const StatUsage = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  margin-top: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percentage: number; warning?: boolean }>`
  height: 100%;
  background: ${props => props.warning ? '#ffc107' : props.percentage >= 100 ? '#dc3545' : '#28a745'};
  border-radius: 4px;
  width: ${props => Math.min(props.percentage, 100)}%;
  transition: width 0.3s ease;
`;

const QuickActions = styled.section`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: #333;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const ActionCard = styled.button`
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #00d4aa;
    background: #f8f9ff;
  }

  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const ActionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #00d4aa20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4aa;
  margin: 0 auto 16px;
`;

const ActionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const ActionDescription = styled.div`
  font-size: 14px;
  color: #666;
`;

const UserBadge = styled.div<{ type: 'owner' | 'employee' }>`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.type === 'owner' ? '#00d4aa20' : '#28a74520'};
  color: ${props => props.type === 'owner' ? '#00d4aa' : '#28a745'};
`;

const TourOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(11, 18, 32, 0.6);
  z-index: 1200;
`;

const TourHighlight = styled.div<{ $top: number; $left: number; $width: number; $height: number }>`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(11, 18, 32, 0.6);
  border: 2px solid #00d4aa;
  pointer-events: none;
  z-index: 1201;
`;

const TourCard = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  width: min(92vw, 360px);
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #dbe3ea;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.28);
  padding: 16px;
  z-index: 1202;
`;

const TourTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 16px;
  color: #0f172a;
`;

const TourDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: #334155;
`;

const TourFooter = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const TourStep = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const TourActions = styled.div`
  display: flex;
  gap: 8px;
`;

const TourButton = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${props => props.$primary ? '#00b894' : '#cbd5e1'};
  background: ${props => props.$primary ? '#00d4aa' : '#fff'};
  color: ${props => props.$primary ? '#fff' : '#334155'};
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
`;

interface PlanUsage {
  stores: { used: number; limit: number; percentage: number };
  customers: { used: number; limit: number; percentage: number };
  rewards: { used: number; limit: number; percentage: number };
  employees: { used: number; limit: number; percentage: number };
}

interface PlanInfo {
  name?: string;
  has_crm?: boolean;
  has_custom_smtp?: boolean;
  has_advanced_reports?: boolean;
  has_tier_program?: boolean;
  has_ai_insights?: boolean;
  has_api_access?: boolean;
  has_partner_network?: boolean;
}

interface TourStepConfig {
  selector: string;
  title: string;
  description: string;
}

export const DashboardPage: React.FC = () => {
  const { user, employee, logout, isOwner } = useAuth();
  const navigate = useNavigate();
  const [planUsage, setPlanUsage] = useState<PlanUsage | null>(null);
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [showTour, setShowTour] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const currentUser = user || employee;
  const displayName = currentUser?.name || 'Usuário';
  const tourStorageKey = `pf_dashboard_tour_v1_${isOwner ? 'owner' : 'employee'}`;

  const tourSteps: TourStepConfig[] = useMemo(() => ([
    { selector: '[data-tour-id="profile"]', title: 'Perfil da Empresa', description: 'Atualize dados da conta, configurações de comunicação e preferências do seu plano.' },
    ...(isOwner ? [{ selector: '[data-tour-id="stores"]', title: 'Lojas', description: 'Cadastre e organize suas unidades para centralizar a operação do programa.' }] : []),
    { selector: '[data-tour-id="customers"]', title: 'Clientes', description: 'Cadastre clientes e mantenha a base ativa para pontuação, resgates e campanhas.' },
    { selector: '[data-tour-id="points"]', title: 'Pontos', description: 'Lance pontos rapidamente e acompanhe o histórico transacional da fidelização.' },
    { selector: '[data-tour-id="rewards"]', title: 'Prêmios', description: 'Configure catálogo, estoque e regras de resgate dos benefícios.' },
    ...(isOwner && planInfo?.has_crm ? [{ selector: '[data-tour-id="crm"]', title: 'CRM e Campanhas', description: 'Automatize jornadas e envie campanhas segmentadas para recompra e retenção.' }] : []),
    ...(isOwner && planInfo?.has_tier_program ? [{ selector: '[data-tour-id="tiers"]', title: 'Programa de Níveis', description: 'Defina tiers, critérios de upgrade e benefícios por nível.' }] : []),
    ...(isOwner && planInfo?.has_ai_insights ? [{ selector: '[data-tour-id="insights"]', title: 'Insights de IA', description: 'Acompanhe churn, CLV e recomendações para decisão mais rápida de retenção.' }] : []),
    ...(isOwner && (planInfo?.has_api_access || planInfo?.has_partner_network) ? [{ selector: '[data-tour-id="integrations"]', title: 'Integrações', description: 'Gerencie API keys, webhooks e parceiros de earn/burn no ecossistema.' }] : []),
    ...(isOwner ? [{ selector: '[data-tour-id="billing"]', title: 'Financeiro', description: 'Gerencie assinatura, faturas e status de cobrança do seu contrato.' }] : []),
  ]), [isOwner, planInfo]);

  useEffect(() => {
    loadPlanUsage();
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    if (localStorage.getItem(tourStorageKey) === 'done') return;
    setShowTour(true);
  }, [currentUser, tourStorageKey]);

  useEffect(() => {
    if (!showTour) return;

    const updateRect = () => {
      const step = tourSteps[activeStepIndex];
      if (!step) {
        setShowTour(false);
        return;
      }

      const element = document.querySelector(step.selector) as HTMLElement | null;
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
      }, 120);
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [showTour, activeStepIndex, tourSteps]);

  const loadPlanUsage = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/dashboard/plan-usage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      console.log('Plan usage data:', data);
      if (data.success) {
        console.log('Setting plan usage:', data.data.usage);
        setPlanUsage(data.data.usage);
        setPlanInfo(data.data.plan || null);
      } else {
        console.error('Plan usage request failed:', data);
      }
    } catch (error) {
      console.error('Erro ao carregar uso do plano:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/app');
  };

  const handleNavigateToStores = () => {
    navigate('/stores');
  };

  const handleNavigateToEmployees = () => {
    navigate('/employees');
  };

  const handleNavigateToCustomers = () => {
    navigate('/customers');
  };

  const handleNavigateToRewards = () => {
    navigate('/rewards');
  };

  const handleNavigateToPoints = () => {
    navigate('/points');
  };

  const handleNavigateToBilling = () => {
    navigate('/billing');
  };

  const handleNavigateToCompanyRules = () => {
    navigate('/company-rules');
  };

  const handleNavigateToCrm = () => {
    navigate('/crm');
  };

  const handleNavigateToReports = () => {
    navigate('/reports');
  };

  const handleNavigateToLoyaltyTiers = () => {
    navigate('/loyalty/tiers');
  };

  const handleNavigateToLoyaltyInsights = () => {
    navigate('/loyalty/insights');
  };

  const handleNavigateToIntegrations = () => {
    navigate('/integrations');
  };

  const closeTour = () => {
    setShowTour(false);
    localStorage.setItem(tourStorageKey, 'done');
  };

  const openTourManually = () => {
    setActiveStepIndex(0);
    setTargetRect(null);
    setShowTour(true);
    localStorage.removeItem(tourStorageKey);
  };

  const goToNextStep = () => {
    if (activeStepIndex >= tourSteps.length - 1) {
      closeTour();
      return;
    }
    setActiveStepIndex((prev) => prev + 1);
  };

  const goToPrevStep = () => {
    setActiveStepIndex((prev) => Math.max(0, prev - 1));
  };

  const activeStep = tourSteps[activeStepIndex];
  const tooltipTop = targetRect ? Math.min(window.innerHeight - 180, targetRect.bottom + 12) : 120;
  const tooltipLeft = targetRect ? Math.max(16, Math.min(window.innerWidth - 380, targetRect.left)) : 16;

  return (
    <DashboardContainer>
      <Header>
        <HeaderContent>
          <Logo>Clube de Fidelidade</Logo>
          <UserInfo>
            <UserBadge type={isOwner ? 'owner' : 'employee'}>
              {isOwner ? 'Proprietário' : 'Funcionário'}
            </UserBadge>
            <UserName>{displayName}</UserName>
            <ProfileButton data-tour-id="profile" onClick={() => navigate('/profile')}>
              <User size={16} />
              Meu Perfil
            </ProfileButton>
            <TourTriggerButton onClick={openTourManually}>
              <FileText size={16} />
              Iniciar Tutorial
            </TourTriggerButton>
            <LogoutButton onClick={handleLogout}>
              <LogOut size={16} />
              Sair
            </LogoutButton>
          </UserInfo>
        </HeaderContent>
      </Header>

      <Main>
        <WelcomeSection>
          <WelcomeTitle>Olá, {displayName}! 👋</WelcomeTitle>
          <WelcomeSubtitle>
            {isOwner 
              ? 'Bem-vindo ao painel de controle do seu sistema de fidelidade.'
              : 'Bem-vindo ao sistema de fidelidade da loja.'
            }
          </WelcomeSubtitle>
        </WelcomeSection>

        <StatsGrid>
          <StatCard>
            <StatIcon color="#00d4aa">
              <Building2 size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>
                {planUsage?.stores?.used ?? 0}
              </StatValue>
              <StatLabel>Lojas</StatLabel>
              {planUsage && planUsage.stores ? (
                <>
                  <StatUsage>
                    {planUsage.stores.used} cadastrados, pode cadastrar mais {planUsage.stores.limit - planUsage.stores.used}
                  </StatUsage>
                  <ProgressBar>
                    <ProgressFill
                      percentage={planUsage.stores.percentage}
                      warning={planUsage.stores.percentage >= 80}
                    />
                  </ProgressBar>
                </>
              ) : (
                <StatUsage>Carregando...</StatUsage>
              )}
            </StatContent>
          </StatCard>

          <StatCard>
            <StatIcon color="#28a745">
              <Users size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>
                {planUsage?.customers?.used ?? 0}
              </StatValue>
              <StatLabel>Clientes</StatLabel>
              {planUsage && planUsage.customers ? (
                <>
                  <StatUsage>
                    {planUsage.customers.used} cadastrados, pode cadastrar mais {planUsage.customers.limit - planUsage.customers.used}
                  </StatUsage>
                  <ProgressBar>
                    <ProgressFill
                      percentage={planUsage.customers.percentage}
                      warning={planUsage.customers.percentage >= 80}
                    />
                  </ProgressBar>
                </>
              ) : (
                <StatUsage>Carregando...</StatUsage>
              )}
            </StatContent>
          </StatCard>

          <StatCard>
            <StatIcon color="#ffc107">
              <Gift size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>
                {planUsage?.rewards?.used ?? 0}
              </StatValue>
              <StatLabel>Prêmios</StatLabel>
              {planUsage && planUsage.rewards ? (
                <>
                  <StatUsage>
                    {planUsage.rewards.used} cadastrados, pode cadastrar mais {planUsage.rewards.limit - planUsage.rewards.used}
                  </StatUsage>
                  <ProgressBar>
                    <ProgressFill
                      percentage={planUsage.rewards.percentage}
                      warning={planUsage.rewards.percentage >= 80}
                    />
                  </ProgressBar>
                </>
              ) : (
                <StatUsage>Carregando...</StatUsage>
              )}
            </StatContent>
          </StatCard>

          <StatCard>
            <StatIcon color="#17a2b8">
              <TrendingUp size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>
                {planUsage?.employees?.used ?? 0}
              </StatValue>
              <StatLabel>Funcionários</StatLabel>
              {planUsage && planUsage.employees ? (
                <>
                  <StatUsage>
                    {planUsage.employees.used} cadastrados, pode cadastrar mais {planUsage.employees.limit - planUsage.employees.used}
                  </StatUsage>
                  <ProgressBar>
                    <ProgressFill
                      percentage={planUsage.employees.percentage}
                      warning={planUsage.employees.percentage >= 80}
                    />
                  </ProgressBar>
                </>
              ) : (
                <StatUsage>Carregando...</StatUsage>
              )}
            </StatContent>
          </StatCard>
        </StatsGrid>

        <QuickActions>
          <SectionTitle>Ações Rápidas</SectionTitle>
          <ActionGrid>
            {isOwner && (
              <ActionCard data-tour-id="stores" onClick={handleNavigateToStores}>
                <ActionIcon>
                  <Building2 size={24} />
                </ActionIcon>
                <ActionTitle>Gerenciar Lojas</ActionTitle>
                <ActionDescription>
                  Adicione e configure suas lojas
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && (
              <ActionCard data-tour-id="billing" onClick={handleNavigateToBilling}>
                <ActionIcon>
                  <CreditCard size={24} />
                </ActionIcon>
                <ActionTitle>Gerenciamento Financeiro</ActionTitle>
                <ActionDescription>
                  Assinatura, faturas e pagamentos
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && (
              <ActionCard onClick={handleNavigateToEmployees}>
                <ActionIcon>
                  <Users size={24} />
                </ActionIcon>
                <ActionTitle>Gerenciar Funcionários</ActionTitle>
                <ActionDescription>
                  Cadastre e gerencie funcionários
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && (
              <ActionCard onClick={handleNavigateToCompanyRules}>
                <ActionIcon>
                  <FileText size={24} />
                </ActionIcon>
                <ActionTitle>Regras do Programa</ActionTitle>
                <ActionDescription>
                  Configure as regras de fidelidade
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && planInfo?.has_crm && (
              <ActionCard data-tour-id="crm" onClick={handleNavigateToCrm}>
                <ActionIcon>
                  <Mail size={24} />
                </ActionIcon>
                <ActionTitle>CRM e Campanhas</ActionTitle>
                <ActionDescription>
                  Envie comunicacoes e acompanhe campanhas
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && planInfo?.has_advanced_reports && (
              <ActionCard onClick={handleNavigateToReports}>
                <ActionIcon>
                  <BarChart3 size={24} />
                </ActionIcon>
                <ActionTitle>Relatórios Avançados</ActionTitle>
                <ActionDescription>
                  Veja tendencias e desempenho do programa
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && planInfo?.has_tier_program && (
              <ActionCard data-tour-id="tiers" onClick={handleNavigateToLoyaltyTiers}>
                <ActionIcon>
                  <Crown size={24} />
                </ActionIcon>
                <ActionTitle>Programa de Níveis</ActionTitle>
                <ActionDescription>
                  Configure tiers, critérios e benefícios por nível
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && planInfo?.has_ai_insights && (
              <ActionCard data-tour-id="insights" onClick={handleNavigateToLoyaltyInsights}>
                <ActionIcon>
                  <Brain size={24} />
                </ActionIcon>
                <ActionTitle>Insights de IA</ActionTitle>
                <ActionDescription>
                  Churn, CLV e recomendações de oferta por comportamento
                </ActionDescription>
              </ActionCard>
            )}

            {isOwner && (planInfo?.has_api_access || planInfo?.has_partner_network) && (
              <ActionCard data-tour-id="integrations" onClick={handleNavigateToIntegrations}>
                <ActionIcon>
                  <PlugZap size={24} />
                </ActionIcon>
                <ActionTitle>Integrações</ActionTitle>
                <ActionDescription>
                  API keys, webhooks e rede de parceiros
                </ActionDescription>
              </ActionCard>
            )}

            <ActionCard data-tour-id="customers" onClick={handleNavigateToCustomers}>
              <ActionIcon>
                <Users size={24} />
              </ActionIcon>
              <ActionTitle>Cadastrar Cliente</ActionTitle>
              <ActionDescription>
                Adicione um novo cliente ao sistema
              </ActionDescription>
            </ActionCard>

            <ActionCard data-tour-id="rewards" onClick={handleNavigateToRewards}>
              <ActionIcon>
                <Gift size={24} />
              </ActionIcon>
              <ActionTitle>Gerenciar Prêmios</ActionTitle>
              <ActionDescription>
                Configure os prêmios disponíveis
              </ActionDescription>
            </ActionCard>

            <ActionCard data-tour-id="points" onClick={handleNavigateToPoints}>
              <ActionIcon>
                <TrendingUp size={24} />
              </ActionIcon>
              <ActionTitle>Adicionar Pontos</ActionTitle>
              <ActionDescription>
                Registre pontos para um cliente
              </ActionDescription>
            </ActionCard>
          </ActionGrid>
        </QuickActions>
      </Main>

      {showTour && activeStep && (
        <>
          <TourOverlay onClick={closeTour} />
          {targetRect && (
            <TourHighlight
              $top={targetRect.top - 6}
              $left={targetRect.left - 6}
              $width={targetRect.width + 12}
              $height={targetRect.height + 12}
            />
          )}
          <TourCard $top={tooltipTop} $left={tooltipLeft}>
            <TourTitle>{activeStep.title}</TourTitle>
            <TourDescription>{activeStep.description}</TourDescription>
            <TourFooter>
              <TourStep>Passo {activeStepIndex + 1} de {tourSteps.length}</TourStep>
              <TourActions>
                <TourButton onClick={closeTour}>Pular</TourButton>
                {activeStepIndex > 0 && (
                  <TourButton onClick={goToPrevStep}>Voltar</TourButton>
                )}
                <TourButton $primary onClick={goToNextStep}>
                  {activeStepIndex >= tourSteps.length - 1 ? 'Concluir' : 'Próximo'}
                </TourButton>
              </TourActions>
            </TourFooter>
          </TourCard>
        </>
      )}
    </DashboardContainer>
  );
};
